import { Injectable, computed, inject } from '@angular/core';
import {
  ref,
  query,
  orderByChild,
  push,
  update,
  remove,
  set,
  get,
  equalTo,
  serverTimestamp,
} from 'firebase/database';
import { environment } from '../../environments/environment';
import { DATABASE, listResource } from '../realtime-data/firebase';
import { AuthService } from '../realtime-data/auth.service';
import { createSpeakerPromoCode } from '../speaker-invite/invite-email';
import { placeholderSessionTitle } from '../speaker-invite/speaker-session-edit';

export interface Session {
  $key?: string;
  room?: string;
  startTime?: string;
  title?: string;
  description?: string;
  track?: string;
  /** Speaker keys. Firebase may return this map as a sparse array. */
  speakers?: Record<string, string>;
  blocks?: number;
  /** Markdown rendered to HTML. Left for Angular to sanitize when bound to [innerHTML]. */
  renderedDescription?: string;
  notes?: string;
  /** Shown on the public site. Speakers' sessions start unpublished, so organizers can review them. */
  published?: boolean;
  /** Only in the session editor; stored separately at adminNotes/<session>. */
  adminNotes?: string;
  /** Server time (ms) of the last save in the admin. */
  updatedAt?: number;
}

export interface Speaker {
  $key?: string;
  name?: string;
  bio?: string;
  /** Markdown rendered to HTML. Left for Angular to sanitize when bound to [innerHTML]. */
  renderedBio?: string;
  published?: boolean;
  company?: string;
  twitter?: string;
  imageUrl?: string;
  website?: string;
  /** Full LinkedIn profile URL. */
  linkedin?: string;
  /** Eventbrite promo code for the speaker's audience, pre-applied by their /s/ link. */
  promoCode?: string;
  /** Server time (ms) of the last save in the admin. */
  updatedAt?: number;
}

export interface Room {
  $key?: string;
  /** What sessions store in their `room`, and the schedule column heading. */
  name: string;
  /** How to find the room from registration, shown on the home page and session pages. */
  directions?: string;
  /** Schedule column order, lowest first. */
  order?: number;
}

/**
 * Stored at speakerInvites/<token>. The token is the secret in the speaker's edit link, so it's
 * random rather than the speaker's key, which is public in the speakers list.
 */
export interface SpeakerInvite {
  $key?: string;
  /** Key of the speaker this invite may edit. */
  speaker: string;
  name: string;
  email: string;
  createdAt?: number;
  /** Server time (ms) the invite was last emailed. */
  emailedAt?: number;
  /** Server time (ms) the share kit was last emailed. */
  kitEmailedAt?: number;
}

export interface Feedback {
  $key?: string;
  speaker: number;
  content: number;
  recommendation: number;
  comment: string;
}

@Injectable({ providedIn: 'root' })
export class DataService {
  db = inject(DATABASE);
  private auth = inject(AuthService);
  private year = environment.year;

  speakers = listResource<Speaker>(
    () => query(this.ref('speakers'), orderByChild('name')),
    '$key',
    { transferKey: `speakers-${this.year}`, localStorageKey: `speakerCache${this.year}` },
  );

  schedule = listResource<Session>(
    () => query(this.ref('schedule'), orderByChild('title')),
    '$key',
    { transferKey: `schedule-${this.year}`, localStorageKey: `sessionsCache${this.year}` },
  );

  private savedRooms = listResource<Room>(
    () => query(this.ref('rooms'), orderByChild('order')),
    '$key',
    {
      transferKey: `rooms-${this.year}`,
      localStorageKey: `roomsCache${this.year}`,
    },
  );

  /**
   * The event's rooms, in schedule order, as edited at /admin/rooms. Until any are saved there,
   * the defaults from environment.venueRooms.
   */
  rooms = computed<Room[]>(() =>
    this.savedRooms().length ? this.savedRooms() : environment.venueRooms,
  );

  /** Whether rooms come from the database rather than the environment defaults. */
  roomsSaved = computed(() => this.savedRooms().length > 0);

  /**
   * Adds a room at the end of the schedule, or returns the existing one with that name (ignoring
   * case). If rooms are still the environment defaults, those are saved first so they're kept.
   */
  async addRoom(name: string): Promise<string> {
    name = name.trim();
    const existing = this.rooms().find((room) => room.name.toLowerCase() === name.toLowerCase());
    if (existing) return existing.name;
    const saved = this.savedRooms();
    const toWrite: Room[] = saved.length
      ? [{ name, order: Math.max(...saved.map((room, i) => room.order ?? i)) + 1 }]
      : [...environment.venueRooms, { name }].map((room, order) => ({ ...room, order }));
    const updates = {};
    for (const room of toWrite) {
      updates[push(this.ref('rooms')).key] = {
        name: room.name,
        directions: room.directions || null,
        order: room.order,
      };
    }
    await update(this.ref('rooms'), updates);
    return name;
  }

  /**
   * Schedule columns: the event's rooms, then any other room a session has been given, so a
   * session in a room nobody has added yet still shows up.
   */
  private venueLayout = computed(() => {
    const rooms = this.rooms().map((room) => room.name);
    const others = this.schedule()
      .filter((session) => session.room && session.track !== 'all' && session.track !== 'Keynote')
      .map((session) => session.room)
      .filter((room) => !rooms.includes(room));
    return { rooms: [...new Set([...rooms, ...others])], floors: {}, hasFloors: false };
  });

  /** Schedule columns, in order. */
  getVenueLayout() {
    return this.venueLayout();
  }

  /**
   * Speakers marked Published in the admin, which are the public ones. The speakers page, nav link,
   * home page section and sitemap entries all appear once there's at least one.
   */
  publishedSpeakers = computed(() =>
    this.speakers().filter((speaker) => speaker.published && speaker.name),
  );

  /** Sessions marked Published in the admin, which are the public ones. */
  publishedSchedule = computed(() => this.schedule().filter((session) => session.published));

  private speakerMap = computed(() => new Map(this.speakers().map((s) => [s.$key, s])));

  speaker(key: string): Speaker | undefined {
    return this.speakerMap().get(key);
  }

  /**
   * Sessions the speaker is presenting, from each session's speakers map (id -> speaker key). Only
   * published ones, unless `unpublished` is set.
   */
  sessionsFor(speakerKey: string | undefined, { unpublished = false } = {}): Session[] {
    if (!speakerKey) return [];
    return (unpublished ? this.schedule() : this.publishedSchedule()).filter((session) =>
      Object.values(session.speakers ?? {}).includes(speakerKey),
    );
  }

  /**
   * Takes in an ISO 8601 datetime string
   * returns a friendly time e.g. "8 PM" in Minnesota Time
   */
  customDateFormatter(isoDateTime) {
    const dateTime = new Date(isoDateTime);
    if (dateTime.toString() != 'Invalid Date') {
      //Check if is actually a DATE.... otherwise look for AMPM
      let time = dateTime.getHours();
      const min = dateTime.getMinutes();
      time -= 6 - dateTime.getTimezoneOffset() / 60;
      const indicator = time >= 12 && time < 24 ? 'PM' : 'AM';
      if (time > 12) {
        time -= 12;
      }
      if (min == 0) {
        return `${time} ${indicator}`;
      } else {
        return `${time}:${String(min).padStart(2, '0')} ${indicator}`;
      }
    } else {
      if (isoDateTime.toString().toLowerCase().indexOf('am') == -1) {
        return 'PM';
      } else {
        return 'AM';
      }
    }
  }

  /** Saves an edited item, or creates one; for a new item the result's key is its id. */
  save(path: 'schedule' | 'speakers', item): PromiseLike<unknown> & { key?: string | null } {
    console.log('Attempting to save', path, item);
    // updatedAt (server time) drives the sitemap's lastmod and busts cached share cards.
    const { $key: key, ...data } = { ...item, updatedAt: serverTimestamp() };
    // Firebase rejects undefined values, so send null (which also clears the field).
    Object.keys(data).forEach((prop) => {
      if (data[prop] === undefined) {
        data[prop] = null;
      }
    });
    if (key) {
      return update(ref(this.db, `devfest${environment.year}/${path}/${key}`), data);
    }
    console.log('Pushing new item to', path, data);
    return push(ref(this.db, `devfest${environment.year}/${path}`), data);
  }

  delete(path: 'schedule' | 'speakers', item) {
    console.log('Attempting to delete', item, 'of type', path);
    const itemRef = ref(this.db, `devfest${environment.year}/${path}/${item.$key}`);
    remove(itemRef);
  }

  deleteSpeakerFromSession(session: Session, speakerKey: string) {
    const speakerRef = ref(
      this.db,
      `devfest${environment.year}/schedule/${session.$key}/speakers/${speakerKey}`,
    );
    remove(speakerRef)
      .then(() => {
        console.log(`Speaker (${speakerKey} deleted from session (${session.$key}) .`);
      })
      .catch((err) => {
        console.error('Error deleting speaker from session', err);
      });
  }

  /**
   * Creates an unpublished speaker, an unpublished session for them to fill in, and an invite, and
   * returns the invite's token. Speakers can edit their sessions but not create them, so each starts
   * with one; an admin adds any others, and sets the time and room.
   */
  async inviteSpeaker(name: string, email: string): Promise<string> {
    const speaker = this.save('speakers', { name, published: false });
    await speaker;
    await this.addPlaceholderSession(name, speaker.key);
    await this.ensureSpeakerPromoCode(speaker.key);
    return this.createSpeakerInvite(speaker.key, name, email);
  }

  /** Gives a speaker their promo code. A failure doesn't lose the speaker; saving them again retries. */
  async ensureSpeakerPromoCode(speakerKey: string) {
    try {
      await createSpeakerPromoCode(await this.auth.auth?.currentUser?.getIdToken(), speakerKey);
    } catch (err) {
      console.error('Unable to create promo code', err);
      alert(`The speaker's promo code wasn't created: ${err.message}. Saving them again retries.`);
    }
  }

  /** Creates a private edit link for an existing speaker without changing their profile or sessions. */
  async createSpeakerInvite(speakerKey: string, name: string, email: string): Promise<string> {
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    const token = btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    await set(this.ref(`speakerInvites/${token}`), {
      speaker: speakerKey,
      name,
      email,
      createdAt: serverTimestamp(),
    });
    return token;
  }

  /** An unpublished session with just a default title and the speaker on it. */
  addPlaceholderSession(name: string, speakerKey: string) {
    return this.save('schedule', {
      title: placeholderSessionTitle(name),
      published: false,
      speakers: { [push(this.ref('schedule')).key]: speakerKey },
    });
  }

  /** Admin only: invites sent to a speaker, so their edit link can be found again. */
  async invitesFor(speakerKey: string): Promise<SpeakerInvite[]> {
    const snapshot = await get(
      query(this.ref('speakerInvites'), orderByChild('speaker'), equalTo(speakerKey)),
    );
    const invites: SpeakerInvite[] = [];
    snapshot.forEach((child) => {
      invites.push({ ...child.val(), $key: child.key });
    });
    return invites;
  }

  ref(path: string) {
    return ref(this.db, `devfest${this.year}/${path}`);
  }
}
